import {
    CYCLE_LAYOUT,
    CYCLE_TIMELINE_LAYOUT,
    CYCLE_TYPOGRAPHY,
    DATE_FORMATS,
    DateInput,
    StepBadge,
} from '@amelio/ui-library';

import {
    deriveDaysAllowedToComplete,
    isLaunchMilestone,
    type NumberedDeadlineMilestone,
} from '../../milestones';
import {
    isDateInPast,
    type DeadlineMilestoneErrors,
    type DeadlineMilestoneValue,
} from '../../validation';
import { ReminderPills, type MilestoneReminderValues } from '../ReminderPills/ReminderPills';

import type { ReactNode } from 'react';

import { FormField } from '@/components/FormField/FormField';
import { Locale } from '@/constants/Locale';
import { useAmelioTranslations } from '@/hooks';
import {
    CYCLE_DATE_FIELD_CHROME,
    CYCLE_FIELD_LABEL,
} from '@/pages/CycleEvaluation/components/cycleFieldChrome';
import i18n from '@/plugins/i18n';

export type { MilestoneReminderValues };

const DEFAULT_MILESTONE_TIME = { hour: 9, minute: 0 };

const ROW_PANEL = [
    `min-w-0 flex-1 ${CYCLE_LAYOUT.tileRadius}`,
    `bg-[var(--color-cycle-tile-idle-surface)] ${CYCLE_TIMELINE_LAYOUT.padding}`,
    'transition-colors hover:bg-[var(--color-cycle-timeline-surface-hover)]',
].join(' ');

const TITLE_TYPE = [
    CYCLE_TYPOGRAPHY.timelineTitle,
    'md:text-[18px] md:leading-[28px]',
    'text-navy',
].join(' ');

/* The library ships compiled CSS, so a utility class this app's own build never emits does not
   exist here — the token is reached through its CSS variable instead. */
const DESCRIPTION_TYPE = [
    'mt-[8px] block',
    `${CYCLE_TIMELINE_LAYOUT.descMax} whitespace-pre-line`,
    CYCLE_TYPOGRAPHY.timelineDesc,
    'text-[var(--color-step-rail-muted)]',
].join(' ');

const getCompletionWindowDays = ({
    value,
}: {
    value?: DeadlineMilestoneValue;
}): number | undefined => {
    const { scheduledAt, dueAt } = value ?? {};

    if (!scheduledAt || !dueAt) {
        return undefined;
    }

    return deriveDaysAllowedToComplete({ scheduledAt, dueAt });
};

const getBadgeVariant = ({
    scheduledAt,
    isOverdue,
}: {
    scheduledAt?: Date;
    isOverdue: boolean;
}): 'primary' | 'inactive' | 'error' => {
    if (!scheduledAt) {
        return 'inactive';
    }

    return isOverdue ? 'error' : 'primary';
};

export interface MilestoneCardProps {
    numbered: NumberedDeadlineMilestone;
    value?: DeadlineMilestoneValue;
    reminders?: MilestoneReminderValues;
    errors?: DeadlineMilestoneErrors;
    isReadOnly?: boolean;
    zIndex?: number;
    sideField?: ReactNode;
    onScheduledAtChange: (date?: Date) => void;
    onDueAtChange?: (date?: Date) => void;
    onRemindersChange?: (values: MilestoneReminderValues) => void;
}

export const MilestoneCard = ({
    numbered,
    value,
    reminders,
    errors,
    isReadOnly = false,
    zIndex,
    sideField,
    onScheduledAtChange,
    onDueAtChange,
    onRemindersChange,
}: MilestoneCardProps) => {
    const { t } = useAmelioTranslations();
    const { milestone, number } = numbered;
    const isLaunch = isLaunchMilestone(milestone);
    const lang = i18n.language === Locale.EN ? Locale.EN : Locale.FR;

    const isOverdue = Boolean(
        value?.scheduledAt && isDateInPast({ date: value.scheduledAt, now: new Date() }),
    );
    const badgeVariant = getBadgeVariant({ scheduledAt: value?.scheduledAt, isOverdue });
    const badgeAriaLabel = isOverdue
        ? `${t('cycleEvaluationDeadlines.milestonePosition', { number })}. ${t('cycleEvaluationDeadlines.validation.pastDate')}`
        : t('cycleEvaluationDeadlines.milestonePosition', { number });

    const handleDueAtChange = (date?: Date) => {
        onDueAtChange?.(date);
    };

    const hasTwoColumns = isLaunch || Boolean(sideField);

    const dateGridWidth = hasTwoColumns
        ? 'responsive-grid-1-2 md:w-[520px]'
        : 'shrink-0 md:w-[260px]';

    const dateFieldSpan = hasTwoColumns ? 'md:col-span-full lg:col-span-1' : '';

    return (
        <div
            className={'relative flex gap-[24px]'}
            style={{ zIndex }}
            data-testid={`milestone-card-${milestone.id}`}
        >
            <StepBadge
                step={number}
                appearance={'timeline'}
                variant={badgeVariant}
                ariaLabel={badgeAriaLabel}
                className={'relative z-10'}
                dataTestId={`milestone-badge-${milestone.id}`}
            />

            <div className={ROW_PANEL}>
                <div className={'flex flex-col max-md:gap-[14px] md:flex-row md:gap-[24px]'}>
                    <div
                        className={`min-w-0 flex-1 ${CYCLE_TIMELINE_LAYOUT.identityMinAtTablet} md:flex md:flex-col md:justify-center`}
                    >
                        <h3 className={TITLE_TYPE}>{t(milestone.labelKey)}</h3>

                        <span className={DESCRIPTION_TYPE}>{t(milestone.descriptionKey)}</span>
                    </div>

                    <div className={`grid gap-[12px] ${dateGridWidth}`}>
                        <FormField
                            label={t(
                                isLaunch
                                    ? 'cycleEvaluationDeadlines.fields.startDateAndTime'
                                    : 'cycleEvaluationDeadlines.fields.dateAndTime',
                            )}
                            errorMessage={
                                errors?.scheduledAt ? t(errors.scheduledAt.key) : undefined
                            }
                            {...CYCLE_FIELD_LABEL}
                            className={`min-w-0 ${dateFieldSpan}`}
                        >
                            <DateInput
                                {...CYCLE_DATE_FIELD_CHROME}
                                portalPanel
                                placeholder={t('cycleEvaluationDeadlines.fields.pickDate')}
                                value={value?.scheduledAt}
                                onChange={onScheduledAtChange}
                                readOnly={isReadOnly}
                                lang={lang}
                                dateFormat={DATE_FORMATS.DISPLAY_DATE}
                                clearAriaLabel={t('cycleEvaluationDeadlines.fields.clearDate')}
                                showTime
                                timeSegment
                                defaultTime={DEFAULT_MILESTONE_TIME}
                                dataTestId={`milestone-scheduled-at-${milestone.id}`}
                            />
                        </FormField>

                        {isLaunch && (
                            <FormField
                                label={t('cycleEvaluationDeadlines.fields.dueDate')}
                                errorMessage={errors?.dueAt ? t(errors.dueAt.key) : undefined}
                                {...CYCLE_FIELD_LABEL}
                                className={`min-w-0 ${dateFieldSpan}`}
                            >
                                <DateInput
                                    {...CYCLE_DATE_FIELD_CHROME}
                                    portalPanel
                                    placeholder={t('cycleEvaluationDeadlines.fields.pickDate')}
                                    value={value?.dueAt}
                                    onChange={handleDueAtChange}
                                    readOnly={isReadOnly}
                                    lang={lang}
                                    dateFormat={DATE_FORMATS.DISPLAY_DATE}
                                    clearAriaLabel={t('cycleEvaluationDeadlines.fields.clearDate')}
                                    dataTestId={`milestone-due-at-${milestone.id}`}
                                />
                            </FormField>
                        )}

                        {sideField && <div className={`min-w-0 ${dateFieldSpan}`}>{sideField}</div>}
                    </div>
                </div>

                {isLaunch && (
                    <ReminderPills
                        milestoneId={milestone.id}
                        reminders={reminders}
                        completionWindowDays={getCompletionWindowDays({ value })}
                        isReadOnly={isReadOnly}
                        onChange={onRemindersChange}
                    />
                )}
            </div>
        </div>
    );
};
